import type ActiveChannel from '@/common/interfaces/ActiveChannel'

export type HostDetails = { hostname: string }

class BffProxyUriService {
  constructor(
    private activeChannel: ActiveChannel,
    private institutionId: string | null,
  ) {}

  /**
   * These are the different services that our http services would previously interpolate ( ex {{launchpad}}/active/service ).
   * so that we could rewrite the path with ones pulled from the launchpad service.
   *
   * We no longer need to do this as we will always be able to assume that all routes will be available at the current host.
   *
   * Going forward, we should make this assumption in our http services and stop using interpolations in code.
   *
   * Once that is complete, and we have removed all the previous interpolations, we can remove the logic specific to this
   * entirely.
   *
   */

  private serviceDictionary = {
    experian: '/v1/experianREST',
    ews: '/v1/ewsREST',
    boltsui: '/v1/ui',
    alloy: '/v1/alloy',
    wolterskluwer: '/v1/wolterskluwer',
    synergy: '/v1/synergy-processor',
    boltsjxchangeaux: '/v1/jxchangeapi-aux',
    boltsfis: '/v1/fischexsystems',
    launchpad: '/v1/launchpadREST',
    boltspigeon: '/v1/pigeon',
    boltsbranch: '/v1/leafREST',
    boltscore: '/v1/jxchangeapi'
  }

  private servicePrefixes = Object.values(this.serviceDictionary)

  private servicePathPrefixRegex = /\/v1\/(.*?)\//

  private serviceRegex = /{{(.*?)}}/

  private employeeBffProxyPrefix = '/openanywhere/bff-employee'
  private applicantBffProxyPrefix = '/openanywhere/bff-applicant'

  private getBffPrefix(): string {
    return this.activeChannel.type == 'EMPLOYEE' ? this.employeeBffProxyPrefix : this.applicantBffProxyPrefix
  }

  private joinPath(...args: string[]): string {
    return args.reduce((acc, curr) => {
      if(curr === '') return acc
      const previousPath = acc.endsWith('/') ? acc : acc + '/'
      const nextPath = curr.startsWith('/') ? curr.substring(1) : curr
      return previousPath + nextPath
    }, '')
  }

  private getInstitutionIdPrefix(): string {
    return this.institutionId ? `/institution/${this.institutionId}` : ``
  }

  /**
   * This acts as a drop in replacement for our current UrlInterpolateInterceptor, the biggest difference is that we are
   * now going to trust that the current host is where all traffic will go rather than pulling the paths from /active/service.
   *
   * Additionally, this will handle inserting the openanywhere bff proxy prefix when the appropriate LD flags are enabled.
   *
   * This will also mean we don't need to do any magic for vite-proxy as we will always be pointing to the current host.
   *
   * @param url
   */
  public parseUrl(url: string | undefined): string {

      if(!url) {
        return ''
      }

      // We need to inject the institution id after the /v1/foo, but before the uri, if it is already fully qualified
      // need to split it apart and inject.
      if(url.startsWith('/v1/')) {
        const [prefix, _] = url.match(this.servicePathPrefixRegex);
        if(!this.servicePrefixes.includes(prefix)) {
          console.error(`Http request made to ${url}, but the service prefix ${prefix} was not found in our service dictionary, this is likely a bug.` )
        }
        return  this.joinPath(this.getBffPrefix(), prefix, this.getInstitutionIdPrefix(), url.replace(prefix, ''))
      }

      const serviceRegexResult = url.match(this.serviceRegex)

      if (serviceRegexResult === null || serviceRegexResult.length == 0) {
        return this.joinPath(this.getBffPrefix(), this.getInstitutionIdPrefix(), url)
      }

      if (serviceRegexResult.length > 2) {
        console.error(
          `Http request made to ${url} contains multiple service interpolations, which is not supported.`,
          serviceRegexResult
        )
        return this.joinPath(this.getBffPrefix(), url)
      }

      // with capture groups, the first element is the full match, the second is the captured group. [{{launchpad}}, launchpad]
      const [fullMatch, service] = serviceRegexResult

      const basePath = this.serviceDictionary[service]

      if (!basePath) {
        console.error(
          `Http request made to ${url}, but the interpolated service: ${fullMatch} was not found in our service dictionary.`
        )
        // for total backwards compatibility with the legacy service, we are returning the original url here.
        return this.joinPath(this.getBffPrefix(), fullMatch, this.getInstitutionIdPrefix(), url.replace(fullMatch, ''))
      }

    const prefix = this.joinPath(this.getBffPrefix(), basePath, this.getInstitutionIdPrefix())
    return url.replace(this.serviceRegex, prefix)

  }

}
export default BffProxyUriService;
